import { Stack, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import {
  getStatusColor,
  mapStatusToProgressBar,
  STEP_LABELS,
  TRANSACTION_STEPS,
} from '../../utils/mapTransactionStatus';
import { Status } from '../../types/Status';
import { ChevronRight } from '@mui/icons-material';

interface ProgressStepProps {
  label: string;
  status: string;
  statusText: string;
  dueDate?: Date;
}

const ProgressStep: React.FC<ProgressStepProps> = ({
  label,
  status,
  statusText,
  dueDate,
}) => {
  return (
    <CustomTooltip title={statusText} width="300px">
      <Stack
        alignItems="center"
        spacing={1}
        sx={{ flex: 1, minWidth: '100px' }}
      >
        <Box
          sx={{
            width: '100%',
            height: 10,
            backgroundColor: getStatusColor(status),
            borderRadius: 5,
            opacity: 1,
          }}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {label}
        </Typography>
        {label === 'Inspections' &&
          status !== 'repairs' &&
          status !== 'completed' &&
          dueDate && (
            <Typography variant="body2" align="center" fontWeight={600}>
              Due by {new Date(dueDate).toLocaleDateString()}
            </Typography>
          )}
      </Stack>
    </CustomTooltip>
  );
};

// New Mobile Step Component
const MobileProgressStep: React.FC<ProgressStepProps> = ({
  label,
  status,
  statusText,
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />;
      case 'pending':
        return (
          <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#fff' }} />
        );
      case 'on hold':
        return <PauseCircleOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />;
      case 'needed':
        return <ErrorOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />;
      default:
        return (
          <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#fff' }} />
        );
    }
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        width: '100%',
        position: 'relative',
        py: 1,
      }}
    >
      {/* Status Circle with Icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: getStatusColor(status),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        {getStatusIcon()}
      </Box>

      {/* Label and Status */}
      <Stack sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'capitalize',
          }}
        >
          {status}
        </Typography>
        <Typography variant="caption">{statusText}</Typography>
      </Stack>
    </Stack>
  );
};

interface TransactionProgressBarProps {
  status: Status;
  isBuyer?: boolean;
  onViewAllSteps?: () => void;
}

const TransactionProgressBar: React.FC<TransactionProgressBarProps> = ({
  status,
  isBuyer,
  onViewAllSteps,
}) => {
  const theme = useTheme();
  const useSmaller = useMediaQuery(theme.breakpoints.down('md'));

  if (useSmaller) {
    const filteredSteps = TRANSACTION_STEPS.filter(
      (step: keyof Status) => (status[step] as any)?.notApplicable !== true
    );

    // Find the closest pending/needed step, excluding 'closing'
    const currentStep =
      [...filteredSteps].find((step) => {
        const stepStatus = mapStatusToProgressBar(step, status).status;
        return (
          step !== 'closing' &&
          (stepStatus === 'pending' ||
            stepStatus === 'needed' ||
            stepStatus === 'repairs')
        );
      }) || filteredSteps[filteredSteps.length - 1];

    return (
      <Stack
        spacing={1}
        direction="row"
        onClick={onViewAllSteps}
        alignItems="center"
      >
        <MobileProgressStep
          key={currentStep}
          label={STEP_LABELS[currentStep]}
          status={mapStatusToProgressBar(currentStep, status).status}
          statusText={
            mapStatusToProgressBar(currentStep, status, isBuyer).statusText
          }
        />
        <ChevronRight />
      </Stack>
    );
  }

  // Desktop version
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems="flex-start"
        sx={{ width: '100%' }}
      >
        {TRANSACTION_STEPS.filter(
          (step: keyof Status) => (status[step] as any)?.notApplicable !== true
        ).map((step) => {
          return (
            <Stack key={step} spacing={1} sx={{ flex: 1, minWidth: '100px' }}>
              <ProgressStep
                label={STEP_LABELS[step]}
                status={mapStatusToProgressBar(step, status).status}
                statusText={
                  mapStatusToProgressBar(step, status, isBuyer).statusText
                }
                dueDate={
                  step === 'inspection' ? status.inspection.dueDate : undefined
                }
              />
              {/* Button for potential actions for each step */}
              {/* {isPBuyer && (
                <Box
                  sx={{
                    display:
                      mapStatusToProgressBar(step, status).status === 'needed'
                        ? 'block'
                        : 'none',
                  }}
                >
                  <TransactionScheduleButton
                    buttonText="Schedule"
                    options={[
                      {
                        label: 'Mark Completed',
                        action: () => {},
                      },
                    ]}
                    sx={{ width: '100%' }}
                  />
                </Box>
              )} */}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default TransactionProgressBar;
